import { makeAutoObservable } from 'mobx';
import { AuthStore } from './auth-store';
import { PlatformStore } from './platform-store';
import { NotificationsStore } from '@/entities/notifications/notifications-store';
import { OnboardingStateStore } from '@/entities/onboarding/onboarding-state-store';

/**
 * Корневой MobX store — единая точка состояния приложения.
 *
 * Заменяет:
 * - Redux store (modules/redux/store.js)
 * - Старый MobX rootStore (store/index.ts)
 *
 * Инициализация делегируется AuthStore:
 * - Есть токен → AuthStore.restoreSession() → грузит profile + platforms
 * - Нет токена → показываем Login
 */
export class RootStore {
  auth: AuthStore;
  platforms: PlatformStore;
  mailbox: NotificationsStore;
  onboarding: OnboardingStateStore;

  profile: any = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    this.platforms = new PlatformStore(this);
    this.auth = new AuthStore(this);
    this.mailbox = new NotificationsStore();
    this.onboarding = new OnboardingStateStore();
  }

  /**
   * Получение отображаемого имени пользователя
   */
  getUserDisplayName(user?: { first_name?: string; last_name?: string }) {
    if (!user) return '';
    return [user.first_name, user.last_name].filter(Boolean).join(' ');
  }

  /**
   * Локализация текста (multi-language объект → строка).
   *
   * API может возвращать ключи в разных форматах:
   * - 'ru-RU', 'en-US' (полный)
   * - 'ru', 'en' (короткий)
   *
   * Пробуем: точное совпадение → короткий код → ru-RU → первый доступный ключ
   */
  localizeText(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value !== 'object') return String(value);

    const lang = this.platforms.activePlatform?.language || 'ru';

    // 1. Точное совпадение (напр. 'ru' или 'ru-RU')
    if (value[lang]) return String(value[lang]);

    // 2. Короткий код → ищем ключ начинающийся с него (ru → ru-RU)
    const shortLang = lang.split('-')[0];
    const matchKey = Object.keys(value).find(
      (k) => k === shortLang || k.startsWith(shortLang + '-'),
    );
    if (matchKey && value[matchKey]) return String(value[matchKey]);

    // 3. Фоллбэк на ru-RU
    if (value['ru-RU']) return String(value['ru-RU']);

    // 4. Первый доступный ключ
    const firstKey = Object.keys(value)[0];
    if (firstKey && value[firstKey]) return String(value[firstKey]);

    return '';
  }
}

export const rootStore = new RootStore();
